import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PACKAGES, ENTITY_TYPES, VAT_STAGGERS, gbp } from "@/lib/constants";
import { BackupCodesDialog } from "@/components/BackupCodesDialog";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const field = "w-full bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none focus:border-[#C9A84A]/60";
const label = "block text-slate-300 text-sm mb-1.5 mt-4";

export default function OnboardClient() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [f, setF] = useState({
    name: "", email: "", entity_type: "sole_trader", package: "essential",
    fee: "", hourly_rate: "", start_date: new Date().toISOString().slice(0, 10),
    year_end: "", incorporation_date: "", company_number: "",
    vat_registered: false, vat_stagger: "1", self_assessment: false, mtd_itsa: false,
    include_earlier: false, assigned_staff_id: "", temp_password: "",
  });

  useEffect(() => { api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isCompany = f.entity_type === "limited_company";
  const isSoleOrLandlord = f.entity_type === "sole_trader" || f.entity_type === "landlord";
  const pkgPrice = f.package === "custom" ? null : PACKAGES[f.package]?.price;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        fee: f.fee === "" ? (pkgPrice ?? 0) : Number(f.fee),
        hourly_rate: f.hourly_rate === "" ? null : Number(f.hourly_rate),
        year_end: f.year_end || null,
        incorporation_date: f.incorporation_date || null,
        company_number: f.company_number || null,
        vat_stagger: f.vat_registered ? f.vat_stagger : null,
        assigned_staff_id: f.assigned_staff_id || null,
        mtd_itsa: isSoleOrLandlord ? f.mtd_itsa : false,
      };
      const { data } = await api.post("/clients", payload);
      setCreated({ client: data.client, deadlines: data.deadlines_created });
      toast.success(`${data.client.name} onboarded · ${data.deadlines_created} deadlines created`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (created) {
    return (
      <div className="max-w-md mx-auto text-center py-10">
        <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
          <Check className="text-emerald-400" size={28} />
        </div>
        <h1 className="font-serif font-bold text-2xl text-white">{created.client.name} is onboarded</h1>
        <p className="text-slate-400 mt-2">{created.deadlines} statutory deadlines created from the start date.</p>
        <div className="card-surface rounded-2xl p-5 mt-6 text-left">
          <p className="text-slate-400 text-sm mb-1">Share these sign-in details securely with the client:</p>
          <p className="text-white"><span className="text-slate-500">Email:</span> {created.client.email}</p>
          <p className="text-white"><span className="text-slate-500">Temporary password:</span> {f.temp_password}</p>
          <p className="text-slate-500 text-xs mt-2">They will be prompted to set a new password on first sign-in.</p>
        </div>
        <div className="flex gap-3 mt-6 justify-center">
          <button onClick={() => navigate(`/clients/${created.client.id}`)} className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-full px-5 py-2.5">Open client</button>
          <button onClick={() => navigate("/clients")} className="card-surface border border-[#1E294B] rounded-full px-5 py-2.5 text-white">All clients</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft size={18} /> Clients</button>
      <h1 className="font-serif font-bold text-3xl text-white mb-6">New client onboarding</h1>

      <form onSubmit={submit} className="card-surface rounded-2xl p-6" data-testid="onboard-form">
        <label className={label}>Business / client name</label>
        <input className={field} required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="onboard-name" />

        <label className={label}>Email</label>
        <input type="email" className={field} required value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="onboard-email" />

        <label className={label}>Entity type</label>
        <select className={field} value={f.entity_type} onChange={(e) => set("entity_type", e.target.value)} data-testid="onboard-entity">
          {ENTITY_TYPES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Package</label>
            <select className={field} value={f.package} onChange={(e) => set("package", e.target.value)} data-testid="onboard-package">
              {Object.entries(PACKAGES).map(([k, v]) => <option key={k} value={k}>{v.name}{v.price ? ` · ${gbp(v.price)}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Monthly fee (£){pkgPrice != null && " — default set"}</label>
            <input type="number" className={field} value={f.fee} placeholder={pkgPrice != null ? String(pkgPrice) : "Custom"} onChange={(e) => set("fee", e.target.value)} data-testid="onboard-fee" />
          </div>
        </div>
        {f.package === "custom" && (
          <>
            <label className={label}>Hourly rate for work outside a package (£)</label>
            <input type="number" className={field} value={f.hourly_rate} placeholder="35" onChange={(e) => set("hourly_rate", e.target.value)} />
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Start date</label>
            <input type="date" className={field} required value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="onboard-start" />
          </div>
          <div>
            <label className={label}>Year end</label>
            <input type="date" className={field} value={f.year_end} onChange={(e) => set("year_end", e.target.value)} />
          </div>
        </div>

        {isCompany && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Incorporation date</label>
              <input type="date" className={field} value={f.incorporation_date} onChange={(e) => set("incorporation_date", e.target.value)} />
            </div>
            <div>
              <label className={label}>Company number</label>
              <input className={field} value={f.company_number} onChange={(e) => set("company_number", e.target.value)} />
            </div>
          </div>
        )}

        <div className="mt-5 space-y-3">
          <Toggle checked={f.vat_registered} onChange={(v) => set("vat_registered", v)} label="VAT registered" testid="onboard-vat" />
          {f.vat_registered && (
            <select className={field} value={f.vat_stagger} onChange={(e) => set("vat_stagger", e.target.value)}>
              {VAT_STAGGERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          )}
          <Toggle checked={f.self_assessment} onChange={(v) => set("self_assessment", v)} label="Self Assessment" testid="onboard-sa" />
          {isSoleOrLandlord && (
            <Toggle checked={f.mtd_itsa} onChange={(v) => set("mtd_itsa", v)} label="MTD for Income Tax (sole traders & landlords only)" testid="onboard-mtd" />
          )}
          <Toggle checked={f.include_earlier} onChange={(v) => set("include_earlier", v)} label="Include earlier deadlines we're responsible for (before the start date)" testid="onboard-earlier" />
        </div>

        <label className={label}>Assign to staff (optional)</label>
        <select className={field} value={f.assigned_staff_id} onChange={(e) => set("assigned_staff_id", e.target.value)}>
          <option value="">Unassigned</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <label className={label}>Temporary password for the client</label>
        <input className={field} required minLength={8} value={f.temp_password} onChange={(e) => set("temp_password", e.target.value)} placeholder="At least 8 characters" data-testid="onboard-temp-password" />

        <button disabled={busy} data-testid="onboard-submit"
          className="w-full mt-6 bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-xl py-3.5 disabled:opacity-60">
          {busy ? "Creating…" : "Onboard client & generate deadlines"}
        </button>
      </form>
    </div>
  );
}

function Toggle({ checked, onChange, label, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className="w-full flex items-center justify-between card-surface border border-[#1E294B] rounded-xl px-4 py-3">
      <span className="text-slate-200 text-sm text-left pr-3">{label}</span>
      <span className={`w-11 h-6 rounded-full shrink-0 transition-colors relative ${checked ? "bg-[#C9A84A]" : "bg-[#1E294B]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}
